import { useEffect, useState, useMemo } from 'react';
import { useLocation } from 'react-router-dom';
import { api } from '@/api/client';
import CoffeeCard from '@/components/coffee/CoffeeCard';
import { Input } from '@/components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Search as SearchIcon, X, Map, List } from 'lucide-react';
import { Skeleton } from '@/components/ui/skeleton';
import MapView from '@/components/coffee/MapView';

const BREW_METHODS = ['Espresso', 'Hand drip', 'Aeropress', 'French press', 'Cold brew'];
const CITIES = ['Bangkok'];

export default function Search() {
  const [coffees, setCoffees] = useState([]);
  const [loading, setLoading] = useState(true);
  const [query, setQuery] = useState('');
  const location = useLocation();
  const [viewMode, setViewMode] = useState('list'); // 'list' | 'map'
  const [filters, setFilters] = useState({
    brew: '',
    city: '',
  });

  useEffect(() => {
    setLoading(true);
    api.entities.Coffee.list('-created_date', 100).then((data) => {
      setCoffees(data);
      setLoading(false);
    });
  }, [location.key]);

  // Unique filter options from data
  const cities = useMemo(() => [...new Set(coffees.map((c) => c.city).filter(Boolean))], [coffees]);

  const activeFilterCount = Object.values(filters).filter(Boolean).length;

  const filtered = useMemo(() => {
    return coffees.filter((c) => {
      const q = query.toLowerCase();
      if (q && !c.name.toLowerCase().includes(q) && !c.cafe_name?.toLowerCase().includes(q) && !c.bean_origin?.toLowerCase().includes(q)) return false;
      if (filters.brew && c.brew_method !== filters.brew) return false;
      if (filters.city && c.city !== filters.city) return false;
      return true;
    });
  }, [coffees, query, filters]);

  const setFilter = (key, val) => setFilters((f) => ({ ...f, [key]: f[key] === val ? '' : val }));
  const clearFilters = () => setFilters({ brew: '', city: '' });

  return (
    <div className="min-h-screen">
      {/* Header */}
      <div className="sticky top-14 z-30 bg-background/95 backdrop-blur border-b border-border px-4 pt-3 pb-3 space-y-3">
        <div className="max-w-5xl mx-auto space-y-3">
        <h1 className="font-heading font-semibold text-xl">Explore Coffees</h1>
        {/* Search + toggles */}
        <div className="flex gap-2">
          <div className="relative flex-1">
            <SearchIcon size={15} className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground" />
            <Input
              value={query}
              onChange={(e) => setQuery(e.target.value)}
              placeholder="Search coffees, cafes..."
              className="pl-9 rounded-xl bg-secondary/50 border-transparent focus:border-primary"
            />
            {query && (
              <button onClick={() => setQuery('')} className="absolute right-3 top-1/2 -translate-y-1/2">
                <X size={14} className="text-muted-foreground" />
              </button>
            )}
          </div>
          <div className="flex rounded-xl border border-border overflow-hidden shrink-0">
            <button
              onClick={() => setViewMode('list')}
              className={`px-2.5 py-2 ${viewMode === 'list' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'}`}
            >
              <List size={16} />
            </button>
            <button
              onClick={() => setViewMode('map')}
              className={`px-2.5 py-2 ${viewMode === 'map' ? 'bg-primary text-primary-foreground' : 'text-muted-foreground hover:bg-secondary'}`}
            >
              <Map size={16} />
            </button>
          </div>
        </div>

        {/* Filters */}
        <div className="space-y-3 pt-1">
          <div className="grid grid-cols-2 gap-2">
            <FilterSelect label="City" allLabel="All Cities" options={cities.length > 0 ? cities : CITIES} value={filters.city} onChange={(v) => setFilter('city', v)} />
            <FilterSelect label="Brew Method" allLabel="All Brew Methods" options={BREW_METHODS} value={filters.brew} onChange={(v) => setFilter('brew', v)} />
          </div>
          {activeFilterCount > 0 && (
            <button onClick={clearFilters} className="text-xs text-primary font-medium hover:underline">
              Clear all filters ({activeFilterCount})
            </button>
          )}
        </div>
        </div>
      </div>

      {/* Results count */}
      <div className="px-4 pt-3 pb-1 max-w-5xl mx-auto">
        <p className="text-xs text-muted-foreground">
          {loading ? 'Loading...' : `${filtered.length} coffee${filtered.length !== 1 ? 's' : ''} found`}
        </p>
      </div>

      {/* Content */}
      {viewMode === 'map' ? (
        <div className="px-4 pb-4 max-w-5xl mx-auto">
          <MapView coffees={filtered} />
        </div>
      ) : (
        <div className="px-4 pb-4 max-w-5xl mx-auto">
          {loading ? (
            <div className="grid grid-cols-2 gap-4 mt-2">
              {Array(6).fill(0).map((_, i) => <Skeleton key={i} className="h-56 rounded-2xl" />)}
            </div>
          ) : filtered.length > 0 ? (
            <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-4 mt-2">
              {filtered.map((coffee) => <CoffeeCard key={coffee.id} coffee={coffee} />)}
            </div>
          ) : (
            <div className="text-center py-16 text-muted-foreground text-sm">
              <SearchIcon size={32} className="mx-auto mb-2 opacity-30" />
              <p>No coffees match your search.</p>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

function FilterSelect({ label, allLabel, options, value, onChange }) {
  return (
    <Select value={value || '__all__'} onValueChange={(v) => onChange(v === '__all__' ? '' : v)}>
      <SelectTrigger className="rounded-xl text-xs h-9 bg-secondary/40 border-transparent">
        <SelectValue placeholder={label} />
      </SelectTrigger>
      <SelectContent>
        <SelectItem value="__all__">{allLabel}</SelectItem>
        {options.map((o) => <SelectItem key={o} value={o}>{o}</SelectItem>)}
      </SelectContent>
    </Select>
  );
}